import { getSsoTenantConfig, ssoEligible } from "../auth/sso";
import account from "../data/account";
import endpoints from "../endpoints";

// Broker-delivered tenantConfig, but ONLY when it belongs to the signed-in
// user's tenant (§215 guard — account.js already does this; feature/billing
// gates must too, or a stale/foreign stored config could flip another
// tenant's features or ad-billing mode on). Token claim is authoritative.
function guardedTenantConfig() {
  let cfg = null;
  try {
    cfg = getSsoTenantConfig();
  } catch (e) {/* no session config */}
  if (!cfg) return null;
  try {
    const tenantId = account.get().tenantId;
    if (tenantId && cfg.tenantId && cfg.tenantId !== tenantId) return null;
  } catch (e) {/* no account yet — keep cfg (pre-render boot) */}
  return cfg;
}

/*
 * Per-tenant feature flags — registry-driven (PlunkTenantConfig.Features,
 * comma-separated; the broker delivers it parsed as tenantConfig.features).
 *
 * Gates built-in surfaces that only apply when the tenant has the
 * underlying data. DEFAULT IS OFF: a tenant without the flag does not get
 * the surface — a new tenant must be deliberately enabled (registry edit,
 * no deploy), never accidentally shown another tenant's UI. This is the
 * same lesson as the MI-page leak.
 *
 * Known flags:
 *   salesTransactions — tenant has a sales feed; shows the Dashboard
 *                       "Sales Transactions" chart (R&S, Ambella).
 *   territoryRep      — tenant has the B2B feeds (sales_rep /
 *                       business_customer / wholesale transactions);
 *                       shows the Territory & Rep Intelligence page
 *                       (manufacturers — Ambella first).
 */
// The tenant's business model from the broker-delivered config (manufacturing | retail | both), or null
// when it has not resolved yet. Synchronous, for menu and tab builders.
export function tenantBusinessModel() {
  const cfg = guardedTenantConfig();
  const bm = cfg && (cfg.businessModel || cfg.BusinessModel);
  return bm ? String(bm).toLowerCase() : null;
}
// Reps, territories and dealers are manufacturer concepts: hidden on a retail-only tenant.
export function sellsThroughReps() {
  const bm = tenantBusinessModel();
  return bm !== "retail";
}
export function getTenantFeatures() {
  const cfg = guardedTenantConfig();
  if (cfg && Array.isArray(cfg.features)) return cfg.features;
  // Localhost dev only (never *.plunk.io — features there always come
  // from the registry): direct-Cognito sessions have no broker-delivered
  // tenantConfig, so feature gating can't know the tenant's real flags.
  // Default to ALL known flags so gated UI is workable in dev. To
  // simulate a tenant's narrower set, override per browser:
  //   localStorage.plunkDevFeatures = 'salesTransactions'   // just these
  //   localStorage.plunkDevFeatures = 'none'                // gate everything
  if (!ssoEligible()) {
    try {
      const raw = localStorage.getItem("plunkDevFeatures");
      if (raw === "none") return [];
      if (raw) return raw.split(",").map(x => x.trim()).filter(Boolean);
    } catch (e) {/* ignore */}
    return ["salesTransactions", "territoryRep", "lifecycle", "bestFit", "campaigns", "socialStats", "contentHub", "productConfigs", "storePerformance", "customReports", "inventory", "quotes", "outreach"];
  }
  return [];
}
export function hasTenantFeature(name) {
  // ── PROD HOLD (host-aware since 09-05, DS): AD-CAMPAIGN surfaces
  // (Campaigns/flights, Campaign Review) are NOT released to production
  // yet — user directive 2026-08-31. staging/dev follow the registry; the
  // hold only binds on the prod host, where the Plunk dogfood tenant
  // (a316487212239) soft-launches ahead of the general release. Full
  // release = delete this block.
  if (name === 'campaigns') {
    const prodHost = typeof location !== 'undefined' && location.host === 'app.plunk.io';
    if (!prodHost) return getTenantFeatures().indexOf('campaigns') > -1;
    try {
      if ((account.get() || {}).tenantId === 'a316487212239') return getTenantFeatures().indexOf('campaigns') > -1;
    } catch (e) {/* fall through to hold */}
    // Released on prod for SELF-SERVICE and SHOPIFY (own-ad-account) workspaces — no Plunk-managed
    // spend involved, and Autopilot lives under Campaigns (user, 2026-09-27). The hold now binds
    // only on MANAGED tenants (Plunk runs their ads).
    try {
      const m = adMode();
      if (m === 'self_hosted' || m === 'self_service') return getTenantFeatures().indexOf('campaigns') > -1;
    } catch (e) {/* fall through to the hold */}
    return false;
  }
  // 'emailAutomation' is Email Automation's own gate (user directive
  // 2026-09-02: released independently of the ad-campaign surfaces). It
  // rides the SAME registry flag ('campaigns') — no new registry seed —
  // and sits OUTSIDE the hold above, so prod ships EA while holding ads.
  if (name === 'emailAutomation') return getTenantFeatures().indexOf('campaigns') > -1;
  return getTenantFeatures().indexOf(name) > -1;
}

// Read-only campaign dashboards (Overview + Markets) — for tenants who should
// SEE ad performance but not campaign details, flights, or create anything
// (e.g. MANAGED tenants — Robb & Stucky, Ambella — to retire the external
// DataStudio dashboards). Opt-IN registry flag `campaignDashboards`.
//
// `campaignDashboards` is a DEFINITIVE read-only RESTRICTION: a tenant that has
// it is dashboard-only regardless of whether it also carries the full
// `campaigns` flag. That matters because these tenants DO keep `campaigns` (it's
// what Email Automation rides, and the prod hold hides full campaigns anyway),
// so the restriction cannot depend on the absence of `campaigns`, and it must
// stay read-only even after the `campaigns` prod hold is eventually removed.
// A tenant with `campaigns` but NOT `campaignDashboards` is full-access as before.
export function hasCampaignDashboards() {
  return hasTenantFeature('campaigns') || getTenantFeatures().indexOf('campaignDashboards') > -1;
}
export function campaignsDashboardOnly() {
  if (getTenantFeatures().indexOf('campaignDashboards') === -1) return false;
  // The read-only restriction is LIFTED on STAGING/dev only, so managed
  // (dashboard) tenants can use flights + campaign details there for
  // testing/demo. It stays in force everywhere else (prod), where these
  // tenants see only the read-only Overview + Markets dashboards.
  try {
    var h = typeof location !== 'undefined' && location.host || '';
    if (h === 'staging.plunk.io' || h.indexOf('localhost') === 0 || h.indexOf('127.0.0.1') === 0) return false;
  } catch (e) {/* default to restricted */}
  return true;
}

// Money controls in the campaign UI (spend, daily budget, Edit campaign, Delete):
// self-managed tenants always; managed tenants ONLY where the dashboard-only
// restriction is lifted (staging/dev) so they can build + budget flights while
// testing. Never on prod for managed tenants (they stay money-free there).
export function campaignMoneyVisible() {
  if (adBillingMode() === 'selfmanaged') return true;
  return getTenantFeatures().indexOf('campaignDashboards') > -1 && !campaignsDashboardOnly();
}

// Ad-billing mode (AD_CAMPAIGNS spec §2.5): 'managed' tenants never see
// cost/spend anywhere in the Campaigns UI; 'selfmanaged' see full money
// columns. DEFAULT IS MANAGED (the safe direction — hiding money from a
// self-managed tenant is an inconvenience; showing spend to a managed
// tenant breaks the platform's pricing model). Server-side omission is
// the real boundary (Phase B); this FE gate is defense-in-depth.
export function adBillingMode() {
  const cfg = guardedTenantConfig();
  if (cfg && cfg.adBilling) return cfg.adBilling;
  if (!ssoEligible()) {
    try {
      const raw = localStorage.getItem("plunkDevAdBilling");
      if (raw) return raw;
    } catch (e) {/* ignore */}
  }
  return "managed";
}

// Tenant ad TYPE (registry AdMode via the broker session): 'managed' |
// 'self_service' (Plunk-managed ad accounts funded by the Ad Wallet) |
// 'self_hosted' (the tenant's OWN Google/Meta accounts, direct-billed —
// Shopify Commerce tenants, 2026-09-26). Default managed (least surface).
export function adMode() {
  const cfg = guardedTenantConfig();
  if (cfg && cfg.adMode) return String(cfg.adMode).toLowerCase().replace(/-/g, '_');
  if (!ssoEligible()) {
    try {
      const raw = localStorage.getItem("plunkDevAdMode");
      if (raw) return raw;
    } catch (e) {/* ignore */}
  }
  return "managed";
}
// Own ad accounts: no Ad Wallet anywhere in the UI.
export function ownAdAccounts() {
  return adMode() === "self_hosted";
}

// Similarity-forecast lens (FORECASTING_PROGRAM.md 2.1): the same run is
// framed for two audiences — 'manufacturer' ("will this design work, and
// who do we sell it to?") vs 'retail' ("will this product sell in my
// stores?"). Registry attribute PlunkTenantConfig.ForecastLens, delivered
// on the broker session; absent → manufacturer (the majority seed).
export function forecastLens() {
  const cfg = guardedTenantConfig();
  if (cfg && cfg.forecastLens) return cfg.forecastLens;
  if (!ssoEligible()) {
    try {
      const raw = localStorage.getItem("plunkDevForecastLens");
      if (raw) return raw;
    } catch (e) {/* ignore */}
  }
  return "manufacturer";
}

// Autopilot rollout gate (DS 2026-09-29): hidden on PROD builds for the listed tenants (R&S until due diligence); staging shows it.
const AUTOPILOT_PROD_HIDDEN = ["anbsezhvanaj"];
export function autopilotHiddenHere() {
  try {
    const root = String(endpoints.product && endpoints.product.root || endpoints.activity && endpoints.activity.root || "");
    const staging = /api\.staging\./i.test(root) || /localhost/i.test(root);
    if (staging) return false;
    const tid = (account.get() || {}).tenantId;
    return !!tid && AUTOPILOT_PROD_HIDDEN.indexOf(String(tid)) >= 0;
  } catch (e) {
    return false;
  }
}

// Manage Data redesign rollout (DS 2026-09-29): the kit layout ships to every tenant on prod EXCEPT the
// Plunk tenant (a316487212239) and the demo workspace Meta's reviewers are walking through (a487844847657, DS). Those two keep the legacy layout on prod builds; staging shows the kit everywhere.
const MANAGE_DATA_LEGACY_PROD = ["a316487212239", "a487844847657"];
export function manageDataLegacyHere() {
  try {
    const root = String(endpoints.product && endpoints.product.root || endpoints.activity && endpoints.activity.root || "");
    const staging = /api\.staging\./i.test(root) || /localhost/i.test(root);
    if (staging) return false;
    // Route-level gates can run before the account has loaded on a cold page load; the SSO tenant
    // recorded at sign-in is the same value and is available immediately.
    let tid = (account.get() || {}).tenantId;
    if (!tid) {
      try {
        tid = localStorage.getItem("plunkSsoTenant");
      } catch (e) {/* no storage */}
    }
    return !!tid && MANAGE_DATA_LEGACY_PROD.indexOf(String(tid)) >= 0;
  } catch (e) {
    return false;
  }
}